import React, { Children, cloneElement, isValidElement, useContext, useMemo, useRef } from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import Trigger from 'choerodon-ui/lib/trigger';
import { Action } from 'choerodon-ui/lib/trigger/enum';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import BUILT_IN_PLACEMENTS from '../trigger-field/placements';
import { ButtonColor, FuncType } from '../button/enum';
import Button from '../button/Button';
import { $l } from '../locale-context';
import isFragment from '../_util/isFragment';
import { iteratorReduce } from '../_util/iteratorUtils';
function getRefCallback(callback, index) {
    return item => callback(item, index);
}
function normalizeAttachments(children, getRef, index = { count: 0 }) {
    return Children.map(children, (child) => {
        if (isFragment(child)) {
            return normalizeAttachments(child.props.children, getRef, index);
        }
        if (isValidElement(child) && child.type.__PRO_ATTACHMENT) {
            const props = { viewMode: 'list', readOnly: true, __inGroup: true };
            if (getRef) {
                const { count } = index;
                props.ref = getRefCallback(getRef, count);
                index.count = count + 1;
            }
            return cloneElement(child, props);
        }
        return undefined;
    });
}
const AttachmentGroup = function AttachmentGroup(props) {
    const { viewMode, children, hidden, text, count, ...buttonProps } = props;
    const hasCount = count !== undefined;
    const { getProPrefixCls, getConfig } = useContext(ConfigContext);
    const listRef = useRef(observable.map());
    const prefixCls = getProPrefixCls('attachment');
    const computedCount = hasCount ? count : iteratorReduce(listRef.current.values(), (sum, attachment) => sum + (attachment.count || 0), 0);
    const attachments = useMemo(() => children ? (React.createElement("div", { className: `${prefixCls}-group` }, normalizeAttachments(children, hasCount ? undefined : action((attachment, index) => {
        if (attachment) {
            listRef.current.set(index, attachment);
        }
        else {
            listRef.current.delete(index);
        }
    })))) : undefined, [children, hasCount, viewMode, prefixCls]);
    const renderEmpty = () => {
        if (computedCount === 0) {
            return (React.createElement("div", { className: `${prefixCls}-empty` }, getConfig('renderEmpty')('Attachment')));
        }
    };
    const content = (React.createElement(React.Fragment, null,
        renderEmpty(),
        attachments));
    const renderGroup = () => {
        if (hidden) {
            return null;
        }
        if (viewMode === 'list') {
            return content;
        }
        return (React.createElement(Trigger, { prefixCls: prefixCls, popupContent: content, action: [Action.hover, Action.focus], builtinPlacements: BUILT_IN_PLACEMENTS, popupPlacement: "bottomLeft", forceRender: !hasCount },
            React.createElement(Button, Object.assign({ icon: "attach_file", funcType: FuncType.link, color: ButtonColor.primary }, buttonProps),
                text || $l('Attachment', 'view_attachment'),
                " ",
                computedCount || undefined)));
    };
    return renderGroup();
};
AttachmentGroup.defaultProps = {
    viewMode: 'popup',
};
AttachmentGroup.displayName = 'AttachmentGroup';
export default observer(AttachmentGroup);
